import React, { useEffect, useState } from 'react'
import { useGlobalState } from '../../hooks/GlobalStateProvider'
import Error from '../generic/Error'
import { ErrorUtils } from '../../utils/siteUtils'
import { isDealerInitiatedOffer } from '../../utils/offerUtils'
import { BodyDivFlex } from '../generic/Basics'
import NavFooter from '../generic/navFooter'
import ICOHeader from '../generic/ICOHeader'
import PageHeader from '../generic/PageHeader'
import ContactInfoSection from './ContactInfoSection'
import VehicleHistorySection from './VehicleHistorySection'
import ConditionTierSection from './ConditionTierSelection'
import AftermarketEquipmentSection from './AftermarketEquipmentSection'
import VehicleDetailsSection from './VehicleDetailsSection'
import VehicleSection from './VehicleSection'
import DealerRepSection from './DealerRepSection'
import VehicleConditionSections from './VehicleConditionSections'
import AdditionalComments from './AdditionalComments'
import ReplacementVehicleSection from './ReplacementVehicleSection'
import TrimMismatchModal from './TrimMismatchModal'
import PaintMeter from './PaintMeterSection'
import EmailMessage from './EmailMessage'
import useHealthCheck from '../../hooks/useHealthCheck'
import MaintenancePage from '../maintenance/Maintenance'
import FyusionImageCapture from '../condition/FyusionImageCapture'

export default props => {
  const [isChanged, setIsChanged] = useState(!props.offer || !!props.offer.isExpired)
  const { globalVariables, updateGlobalVariables } = useGlobalState()
  const { experienceState, urlState, offer, globalState, updateGlobalState, navigatePageWithState, vinModalInfo, year, make, model, prospect, trim, plateStateVinError, setShowModal, bodyStyle } = props
  const { dealerInfo, isVehicleGrounded, shouldShowOfferCreationComponent, isIneligibleVehiclesEnabled, isAssigned, vehicleIntoAuction, customerInfo } = experienceState
  const isConsumerOffer = dealerInfo && dealerInfo.isConsumerOffer
  const [renderSubmitHydrator, setRenderSubmitHydrator] = useState(false)
  const canEdit = true // Handled by DAT
  const contactInfo = globalState.firstName ? globalState : customerInfo.firstName ? customerInfo : prospect && prospect.contactInfo.firstName ? prospect.contactInfo : {}
  const { misMatch } = vinModalInfo
  const reviewPageScrollY = globalState.reviewPageScrollY
  const trimMismatchOverride = offer && offer.prospect.vehicle.vin && offer.prospect.vehicle.vin === urlState.vin && !plateStateVinError
  const navigatePageWithStateNew = state => {
    //clear the error when user navigates to other page
    //Don't navigate unless the trims match
    if (misMatch && trimMismatchOverride && !globalState.resetTrim && !state.resetTrim) {
      setShowModal(true)
    } else {
      updateGlobalState({ reviewPageScrollY: window.scrollY, historyState: { year, make, model, urlState }, offerError: undefined })
      navigatePageWithState(state)
    }
  }

  const newProps = { navigatePageWithState: navigatePageWithStateNew, displayImages: experienceState && experienceState.displayImages, canEdit }

  useEffect(() => {
    if (reviewPageScrollY && window.scrollY !== reviewPageScrollY)
      setTimeout(() => {
        if (reviewPageScrollY && window.scrollY !== reviewPageScrollY) {
          window.scrollTo(0, reviewPageScrollY)
          updateGlobalState({ reviewPageScrollY: undefined })
        }
      }, 150)
  }, [updateGlobalState, reviewPageScrollY])

  const offerExists = !!offer && !offer.quickQuote
  useEffect(() => {
    if (!offerExists) updateGlobalState({ reviewReached: true })
  }, [updateGlobalState, offerExists])

  const calculateViewChange = (name, uniqueViewStr) => {
    if (!!uniqueViewStr) {
      name = name + 'Review'
      const originalValue = globalVariables.current[name]
      if (!originalValue) updateGlobalVariables({ [name]: uniqueViewStr })
      else if (originalValue !== uniqueViewStr) !isChanged && setIsChanged(true)
      else if (offer && trim && offer.prospect.vehicle.trimId !== trim.trimId) {
        setIsChanged(true)
      }
    }
  }

  useEffect(() => {
    //check for missing email/phone before user submits
    isChanged && !contactInfo.email && isConsumerOffer && updateGlobalState({ offerError: 400082 })
    isChanged && !contactInfo.phone && isConsumerOffer && updateGlobalState({ offerError: 400084 })
  }, [isChanged, updateGlobalState, contactInfo.email, contactInfo.phone, isConsumerOffer])

  function getNextClick(e) {
    const { nextClick } = props
    setRenderSubmitHydrator(shouldShowOfferCreationComponent)
    nextClick(e)
  }

  const allprops = { ...props, ...newProps, calculateViewChange, nextClick: (...e) => getNextClick(...e) }
  const errorType = globalState.offerError && ErrorUtils.getErrorType(globalState.offerError)
  const nextDisabled = (misMatch && !globalState.resetTrim) || (!isChanged && !offer.quickQuote)
  const showPaintMeter = false
  const isDIOffer = isDealerInitiatedOffer(offer?.offerSourceId)
  const isTaasOffer = offer?.offerCode?.toUpperCase() === 'TC'
  const showEmailMessage = !(isDIOffer || isTaasOffer)
  const isArtBased = props.eligibility?.isArtBased
  const landingHealth = props.health
  const underMaintenance = useHealthCheck({ includeManheim: isArtBased, reuse: true, landingContextHealth: landingHealth })
  const allowIneligibleVehiclesForWebComponent = isIneligibleVehiclesEnabled && isAssigned && !vehicleIntoAuction
  const dealerGuid = experienceState.dealerGuid || urlState.dealer_id
  const vin = globalState.vin || urlState.vin || offer?.prospect?.vehicle?.vin
  return underMaintenance ? (
    <MaintenancePage />
  ) : (
    <>
      <BodyDivFlex>
        <br />
        {globalState.isFyusionPhotoCaptureExperienceEnabled && <FyusionImageCapture vehicleBodyStyle={bodyStyle} vin={vin} dealerGuid={dealerGuid} />}
        <ICOHeader {...allprops} showProgress={!offer} />
        <PageHeader text={(offer ? 'Edit' : 'Review') + (offer && offer.quickQuote ? ' Quick Quote' : ' Offer')} />
        {globalState.offerError && <Error text={`${ErrorUtils.translateError(globalState.offerError)}`} isError={true} />}
        <br />
        <DealerRepSection {...allprops} />
        <ContactInfoSection {...allprops} contactInfo={contactInfo} isError={errorType === 'emailErrors' || errorType === 'phoneErrors'} />
        <VehicleSection {...allprops} />
        <VehicleDetailsSection {...allprops} />
        <AftermarketEquipmentSection {...allprops} />
        <VehicleHistorySection {...allprops} />
        {offer && offer.conditionTierSelection && <ConditionTierSection setShowModal={setShowModal} setIsChanged={setIsChanged} {...allprops} />}
        <VehicleConditionSections {...allprops} />
        {showPaintMeter && <PaintMeter {...allprops} />}
        <AdditionalComments {...allprops} isVehicleGrounded={isVehicleGrounded} />
        <ReplacementVehicleSection {...allprops} />
        <TrimMismatchModal {...allprops} />
        {offer && showEmailMessage && <EmailMessage {...allprops} />}
      </BodyDivFlex>
      {renderSubmitHydrator && <offer-submit-hydrator clear-state-on-failure={false} allow-ineligible-vehicle-submission={allowIneligibleVehiclesForWebComponent} />}
      <NavFooter {...allprops} nextDisabled={!canEdit || nextDisabled} step={urlState.p} />
    </>
  )
}
